import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/common";
import AnonymityNotice from "@/components/AnonymityNotice";
import FormSection from "@/components/FormSection";
import { AutosaveIndicator, RestoreDraftBanner } from "@/components/AutosaveIndicator";
import useAutosave from "@/hooks/useAutosave";

const AUTOSAVE_KEY = "rfq-create-draft-v1";

const INITIAL = {
  product_name: "", category: "", spec: "", quantity: 1, unit: "each",
  delivery_region: "Ontario", urgency: "normal", expected_delivery_days: 14,
  notes: "", brand_preference: "", allow_alternates: true, expires_in_hours: 72,
};

export default function RfqCreate() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [cats, setCats] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [touched, setTouched] = useState({});
  const [form, setForm] = useState(INITIAL);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const markTouched = (k) => setTouched(t => ({ ...t, [k]: true }));

  // Validation derived from current form state.
  const errors = useMemo(() => {
    const e = {};
    if (!form.product_name?.trim()) e.product_name = "Required";
    else if (form.product_name.trim().length < 3) e.product_name = "Use a recognisable part name (≥ 3 characters).";
    if (!form.category) e.category = "Pick a category so we can match suppliers.";
    const qty = parseInt(form.quantity);
    if (!qty || qty < 1) e.quantity = "Quantity must be at least 1.";
    const eta = parseInt(form.expected_delivery_days);
    if (!eta || eta < 1) e.expected_delivery_days = "Must be ≥ 1 day.";
    const validHrs = parseInt(form.expires_in_hours);
    if (!validHrs || validHrs < 1) e.expires_in_hours = "Must be ≥ 1 hour.";
    return e;
  }, [form]);

  const hasErrors = Object.keys(errors).length > 0;

  useEffect(() => {
    api.get("/meta/categories").then(r => setCats(r.data));
    const pre = params.get("prefill");
    if (pre) {
      try { const p = JSON.parse(pre); setForm(f => ({ ...f, ...p })); } catch { /* invalid prefill */ }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Autosave — engage as soon as a meaningful field is filled.
  const { restored, status, lastSavedAt, clear, dismissRestore } = useAutosave({
    storageKey: AUTOSAVE_KEY,
    data: form,
    isEmpty: (d) => !d.product_name?.trim() && !d.spec?.trim() && !d.notes?.trim() && !d.category,
  });

  const resumeDraft = () => {
    if (restored?.data) setForm(f => ({ ...f, ...restored.data }));
    dismissRestore();
  };

  const doSubmit = async (isDraft) => {
    setErr("");
    // Final validation on submit (drafts skip strict checks except product_name + category).
    const blocking = isDraft
      ? (!form.product_name?.trim() ? { product_name: "Required for a draft" } : {})
      : errors;
    if (Object.keys(blocking).length) {
      setTouched({ product_name: true, category: true, quantity: true, expected_delivery_days: true, expires_in_hours: true });
      setErr("Please fix the highlighted fields before submitting.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        quantity: parseInt(form.quantity),
        expected_delivery_days: parseInt(form.expected_delivery_days),
        expires_in_hours: parseInt(form.expires_in_hours),
      };
      const { data } = await api.post(`/rfqs${isDraft ? "?draft=true" : ""}`, payload);
      clear();
      nav(`/app/rfqs/${data.id}`);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const submit = (e) => { e.preventDefault(); doSubmit(false); };
  const saveDraft = () => doSubmit(true);

  const cancel = () => {
    if (window.confirm("Discard this RFQ draft and leave?")) {
      clear();
      nav(-1);
    }
  };

  const showError = (k) => touched[k] && errors[k];

  return (
    <>
      <PageHeader title="Create an RFQ" subtitle="Structured requests mean better, more comparable quotes. Your draft autosaves as you type." />
      <AnonymityNotice context="rfq" />

      {restored && (
        <div style={{ maxWidth: 820, marginBottom: 14 }}>
          <RestoreDraftBanner savedAt={restored.savedAt} onResume={resumeDraft} onDiscard={() => { dismissRestore(); }} />
        </div>
      )}

      <form
        onSubmit={submit}
        className="card rfq-create-form"
        style={{ padding: "clamp(18px, 4vw, 28px)", maxWidth: 820, display: "grid", gap: 22 }}
        data-testid="rfq-create-form"
      >
        <div className="form-card-header">
          <span className="font-mono" style={{ fontSize: 11, letterSpacing: ".15em", color: "var(--ink-mute)" }}>NEW REQUEST FOR QUOTATION</span>
          <AutosaveIndicator status={status} lastSavedAt={lastSavedAt} />
        </div>

        <FormSection index={1} title="Product details" hint="What needs to be sourced? Be specific — the part name, category, and any spec drive supplier matching." testId="sec-product">
          <div className="rfq-grid-2">
            <div className="field">
              <label className="label">Product / part name *</label>
              <input
                className={`input ${showError("product_name") ? "input-error" : ""}`}
                data-testid="rfq-product"
                value={form.product_name}
                onChange={e => set("product_name", e.target.value)}
                onBlur={() => markTouched("product_name")}
                placeholder="e.g. SKF 6205-2RS Deep Groove Bearing"
                required
              />
              {showError("product_name") && <div className="field-error" data-testid="err-product">{errors.product_name}</div>}
            </div>
            <div className="field">
              <label className="label">Category *</label>
              <select
                className={`input ${showError("category") ? "input-error" : ""}`}
                data-testid="rfq-category"
                value={form.category}
                onChange={e => set("category", e.target.value)}
                onBlur={() => markTouched("category")}
                required
              >
                <option value="">Select a category…</option>
                {cats.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              {showError("category") && <div className="field-error" data-testid="err-category">{errors.category}</div>}
            </div>
          </div>
          <div className="field">
            <label className="label">Specification / requirements</label>
            <input className="input" data-testid="rfq-spec" value={form.spec} onChange={e => set("spec", e.target.value)} placeholder="e.g. C3 clearance, OEM grade" />
            <div className="field-hint">Tolerances, certifications, OEM-grade only, etc. Avoid sharing contact info — anonymity policy applies.</div>
          </div>
          <div className="field">
            <label className="label">Brand preference (optional)</label>
            <input className="input" value={form.brand_preference} onChange={e => set("brand_preference", e.target.value)} placeholder="e.g. SKF, Timken — leave blank if open" />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <input id="alt" type="checkbox" checked={form.allow_alternates} onChange={e => set("allow_alternates", e.target.checked)} />
            <label htmlFor="alt" style={{ fontSize: 13, color: "var(--ink-dim)" }}>Allow supplier alternates / substitutes</label>
          </div>
        </FormSection>

        <FormSection index={2} title="Quantity & logistics" hint="How much, where it needs to land, and how soon." testId="sec-logistics">
          <div className="rfq-grid-4">
            <div className="field">
              <label className="label">Quantity *</label>
              <input
                className={`input ${showError("quantity") ? "input-error" : ""}`}
                type="number" min={1} data-testid="rfq-qty" value={form.quantity}
                onChange={e => set("quantity", e.target.value)} onBlur={() => markTouched("quantity")} required
              />
              {showError("quantity") && <div className="field-error">{errors.quantity}</div>}
            </div>
            <div className="field">
              <label className="label">Unit</label>
              <select className="input" value={form.unit} onChange={e => set("unit", e.target.value)}>
                {["each", "pack", "m", "kg", "L", "set", "box"].map(u => <option key={u}>{u}</option>)}
              </select>
            </div>
            <div className="field">
              <label className="label">Delivery region *</label>
              <select className="input" data-testid="rfq-region" value={form.delivery_region} onChange={e => set("delivery_region", e.target.value)}>
                {["Ontario", "Quebec", "British Columbia", "Alberta", "Manitoba", "Saskatchewan", "Nova Scotia"].map(r => <option key={r}>{r}</option>)}
              </select>
            </div>
            <div className="field">
              <label className="label">Urgency</label>
              <select className="input" value={form.urgency} onChange={e => set("urgency", e.target.value)}>
                {["low", "normal", "high", "critical"].map(u => <option key={u}>{u}</option>)}
              </select>
            </div>
          </div>
          <div className="rfq-grid-2">
            <div className="field">
              <label className="label">Expected delivery (days)</label>
              <input
                className={`input ${showError("expected_delivery_days") ? "input-error" : ""}`}
                type="number" min={1} value={form.expected_delivery_days}
                onChange={e => set("expected_delivery_days", e.target.value)} onBlur={() => markTouched("expected_delivery_days")}
              />
              {showError("expected_delivery_days") && <div className="field-error">{errors.expected_delivery_days}</div>}
            </div>
            <div className="field">
              <label className="label">RFQ validity (hours)</label>
              <input
                className={`input ${showError("expires_in_hours") ? "input-error" : ""}`}
                type="number" min={1} value={form.expires_in_hours}
                onChange={e => set("expires_in_hours", e.target.value)} onBlur={() => markTouched("expires_in_hours")}
              />
              <div className="field-hint">After this window the RFQ auto-expires and supplier quotes are locked.</div>
            </div>
          </div>
        </FormSection>

        <FormSection index={3} title="Notes for suppliers" hint="Optional context. Mounting details, tolerances, certifications, batch / lot constraints." testId="sec-notes">
          <div className="field">
            <label className="label">Notes / special instructions</label>
            <textarea
              className="input"
              style={{ minHeight: 120, resize: "vertical" }}
              data-testid="rfq-notes"
              value={form.notes}
              onChange={e => set("notes", e.target.value)}
              placeholder="e.g. Need OEM-grade only, must include serial cert. Anonymity reminder: don't include email / phone / company name."
            />
            <div className="field-hint">Anonymity is enforced — emails, phone numbers, URLs, and company names are auto-blocked.</div>
          </div>
        </FormSection>

        {err && <div style={{ color: "var(--red)", fontSize: 13 }} data-testid="rfq-form-error">{err}</div>}

        <div className="form-action-row form-action-row-sticky">
          <button
            type="submit"
            className="btn btn-primary"
            data-testid="rfq-submit"
            disabled={busy || hasErrors}
            title={hasErrors ? "Fix highlighted fields to enable submit" : ""}
          >
            {busy ? "Submitting…" : "Submit RFQ"}
          </button>
          <button type="button" className="btn btn-ghost" data-testid="rfq-save-draft" onClick={saveDraft} disabled={busy}>Save as draft</button>
          <button type="button" className="btn btn-ghost" onClick={cancel}>Cancel</button>
        </div>
      </form>
    </>
  );
}
